<template>
  <!-- 设备维护新增/编辑表单 -->
  <a-modal
    :title="formTitle || '新增维护记录'"
    v-model:visible="open"
    @cancel="onClose"
    :width="1000"
    :maskClosable="false"
    :keyboard="false"
    :destroyOnClose="true"
    :footer="null"
    class="create-form-modal"
  >
    <a-form-model
      ref="form"
      :model="form"
      :rules="rules"
      :label-col="{ flex: '0 0 150px' }"
      :wrapper-col="{ flex: '1 1 auto' }"
    >
      <a-row :gutter="24">
        <!-- 左列 -->
        <a-col :span="12">
          <!-- 设备名称/手环MAC：根据设备类型动态显示标签，自动填充且禁用 -->
          <a-form-model-item :label="nameLabel" prop="name">
            <a-input v-model:value="form.name" placeholder="自动填入" disabled />
          </a-form-model-item>

          <!-- 故障现象 -->
          <a-form-model-item label="故障现象" prop="malfunctionSymptoms">
            <a-input
              v-model:value="form.malfunctionSymptoms"
              placeholder="请输入故障现象"
            />
          </a-form-model-item>

          <!-- 维修费用：数字输入框，最小值为0，步长0.01，保留2位小数 -->
          <a-form-model-item label="维修费用" prop="price">
            <a-input-number
              v-model:value="form.price"
              :min="0"
              :step="0.01"
              :precision="2"
              placeholder="请输入维修费用"
              style="width: 100%"
            />
          </a-form-model-item>

          <!-- 时间 -->
          <a-form-model-item label="时间" prop="operationTime">
            <a-date-picker
              style="width: 100%"
              v-model:value="form.operationTime"
              format="YYYY-MM-DD HH:mm:ss"
              :show-time="{ format: 'HH:mm:ss' }"
              valueFormat="YYYY-MM-DD HH:mm:ss"
              allow-clear
            />
          </a-form-model-item>
        </a-col>

        <!-- 右列 -->
        <a-col :span="12">
          <!-- 故障原因 -->
          <a-form-model-item label="故障原因" prop="causeOfFailure">
            <a-input
              v-model:value="form.causeOfFailure"
              placeholder="请输入故障原因"
            />
          </a-form-model-item>

          <!-- 维保状态：字典下拉选择 -->
          <a-form-model-item label="维保状态" prop="maintenanceStatus">
            <a-select
              v-model:value="form.maintenanceStatus"
              placeholder="请选择维保状态"
              allow-clear
              style="width: 100%"
            >
              <a-select-option
                v-for="(d, index) in dict.type['maintenance_status']"
                :key="index"
                :value="d.value"
              >
                {{ d.label }}
              </a-select-option>
            </a-select>
          </a-form-model-item>

          <!-- 维修物流信息 -->
          <a-form-model-item label="维修物流信息" prop="logisticsInformation">
            <a-input
              v-model:value="form.logisticsInformation"
              placeholder="请输入维修物流信息"
            />
          </a-form-model-item>
        </a-col>
      </a-row>

      <!-- 操作按钮 -->
      <div class="bottom-control">
        <a-button type="primary" :loading="submitLoading" @click="submitForm" class="save-btn">
          保存
        </a-button>
        <a-button @click="cancel" class="cancel-btn">取消</a-button>
      </div>
    </a-form-model>
  </a-modal>
</template>

<script>
import { $on, $off, $once, $emit } from '../../../../utils/gogocodeTransfer'
/**
 * 设备维护新增/编辑表单组件
 * 功能：
 * - 支持新增和编辑设备维护记录
 * - 从设备维护页面调用时，自动填充设备名称、设备类型和maintenanceId
 * - 根据设备类型动态显示标签（设备名称/手环MAC）
 */
import {
  getEquipmentMaintenance,
  addEquipmentMaintenance,
  updateEquipmentMaintenance,
} from '@/api/system/equipmentMaintenance'

export default {
  name: 'EquipmentMaintenanceCreateForm',
  // 字典类型配置
  dicts: ['maintenance_type', 'maintenance_status'],
  props: {
    /**
     * 设备ID（可选）
     * 当从设备维护页面调用时传入，用于自动设置maintenanceId和设备类型
     */
    equipmentId: {
      type: Number,
      default: null,
    },
    /**
     * 设备名称（可选）
     * 当从设备维护页面调用时传入，用于自动填充设备名称字段
     */
    equipmentName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      // 提交按钮加载状态
      submitLoading: false,
      // 表单标题（添加/修改）
      formTitle: '',
      // 表单数据
      form: {
        id: null, // 维护记录ID（编辑时使用）
        type: null, // 设备类型（设备维护/手环维护）
        name: null, // 设备名称/手环MAC（自动填充，禁用）
        malfunctionSymptoms: null, // 故障现象
        causeOfFailure: null, // 故障原因
        price: null, // 维修费用
        maintenanceStatus: undefined, // 维保状态（字典值）
        logisticsInformation: null, // 维修物流信息
        operationTime: null, // 操作时间
        maintenanceId: null, // 维护ID（关联设备ID，从调用方传入）
      },
      // 表单类型：1-新增，2-修改
      formType: 1,
      // 弹窗显示/隐藏状态
      open: false,
      // 表单验证规则
      rules: {
        // 设备名称/手环MAC：必填
        name: [
          {
            required: true,
            message: '请输入设备名称/手环MAC',
            trigger: 'blur',
          },
        ],
        // 故障现象：必填
        malfunctionSymptoms: [
          { required: true, message: '请输入故障现象', trigger: 'blur' },
        ],
        // 故障原因：必填
        causeOfFailure: [
          { required: true, message: '请输入故障原因', trigger: 'blur' },
        ],
        // 维修费用：必填
        price: [
          { required: true, message: '请输入维修费用', trigger: 'blur' },
          {
            type: 'number',
            min: 0,
            message: '维修费用不能小于0',
            trigger: 'blur',
          },
        ],
        // 维保状态：必填
        maintenanceStatus: [
          { required: true, message: '请选择维保状态', trigger: 'change' },
        ],
        // 维修物流信息：必填
        logisticsInformation: [
          { required: true, message: '请输入维修物流信息', trigger: 'blur' },
        ],
        // 时间：必填
        operationTime: [
          { required: true, message: '请选择时间', trigger: 'change' },
        ],
      },
    }
  },
  computed: {
    /**
     * 设备类型是否禁用
     * 当从设备维护页面调用时（equipmentId存在），设备类型应该禁用并自动填充为"设备维护"
     */
    typeDisabled() {
      return !!this.equipmentId
    },

    /**
     * 根据设备类型动态显示标签
     * - 如果设备类型是"设备维护"，显示"设备名称"
     * - 如果设备类型是"手环维护"，显示"手环MAC"
     * - 如果未选择设备类型，显示"手环MAC/设备名称"
     */
    nameLabel() {
      if (!this.form.type) {
        return '手环MAC/设备名称'
      }
      // 从字典中查找设备类型对应的标签
      const maintenanceTypeDict = this.dict.type['maintenance_type'] || []
      const currentType = maintenanceTypeDict.find(
        (item) => item.value === this.form.type
      )
      if (currentType) {
        if (currentType.label === '设备维护') {
          return '设备名称'
        } else if (currentType.label === '手环维护') {
          return '手环MAC'
        }
      }
      return '手环MAC/设备名称'
    },
  },
  methods: {
    /**
     * 关闭弹窗
     */
    onClose() {
      this.open = false
    },

    /**
     * 取消按钮
     * 关闭弹窗并重置表单
     */
    cancel() {
      this.open = false
      this.reset()
    },

    /**
     * 表单重置
     * 清空所有表单字段，重置为初始状态
     */
    reset() {
      this.formType = 1
      this.form = {
        id: null,
        type: null,
        name: null,
        malfunctionSymptoms: null,
        causeOfFailure: null,
        price: null,
        maintenanceStatus: undefined,
        logisticsInformation: null,
        operationTime: null,
        maintenanceId: null,
      }
    },

    /**
     * 新增按钮操作
     * 打开新增表单，自动填充设备名称、设备类型和maintenanceId
     * @param {Number} equipmentId - 设备ID（可选，从调用方传入）
     * @param {String} equipmentName - 设备名称（可选，从调用方传入）
     */
    handleAdd(equipmentId, equipmentName) {
      this.reset()
      this.formType = 1

      // 自动填充设备名称
      if (equipmentName) {
        this.form.name = equipmentName
      } else if (this.equipmentName) {
        // 如果没有传入参数，但props中有设备名称，则使用props中的值
        this.form.name = this.equipmentName
      }

      // 如果从设备维护页面调用（equipmentId存在），则自动设置设备类型为"设备维护"并设置maintenanceId
      if (equipmentId || this.equipmentId) {
        // 设置维护ID（来自调用的设备ID）
        this.form.maintenanceId = equipmentId || this.equipmentId
        // 从字典中查找 label 为"设备维护"的项，获取其 value
        const maintenanceTypeDict = this.dict.type['maintenance_type'] || []
        const equipmentMaintenanceType = maintenanceTypeDict.find(
          (item) => item.label === '设备维护'
        )
        if (equipmentMaintenanceType) {
          this.form.type = equipmentMaintenanceType.value
        }
      }

      this.open = true
      this.formTitle = '添加'
    },

    /**
     * 修改按钮操作
     * 打开编辑表单，加载指定记录的数据
     * @param {Object} row - 行数据对象（包含id字段）
     * @param {Number|Array} ids - ID或ID数组（当row为空时使用）
     */
    handleUpdate(row, ids) {
      this.reset()
      this.formType = 2
      const id = row ? row.id : ids

      // 获取维护记录详情
      getEquipmentMaintenance({ id: id }).then((response) => {
        this.form = response.data
        this.open = true
        this.formTitle = '修改'
      })
    },

    /**
     * 提交表单
     * 根据formType判断是新增还是修改，调用相应的API
     */
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.submitLoading = true

          // 判断是修改还是新增
          if (this.form.id !== undefined && this.form.id !== null) {
            // 修改
            updateEquipmentMaintenance(this.form)
              .then((response) => {
                this.$message.success('修改成功', 3)
                this.open = false
                $emit(this, 'ok')
              })
              .finally(() => {
                this.submitLoading = false
              })
          } else {
            // 新增
            addEquipmentMaintenance(this.form)
              .then((response) => {
                this.$message.success('新增成功', 3)
                this.open = false
                $emit(this, 'ok')
              })
              .finally(() => {
                this.submitLoading = false
              })
          }
        } else {
          return false
        }
      })
    },
  },
  emits: ['ok'],
}
</script>

<style scoped>
.bottom-control {
  text-align: right;
  border-top: 1px solid #f0f0f0;
}

.create-form-modal :deep(.ant-modal-body) {
  padding: 20px 16px 80px 16px !important;
}

.save-btn {
  background-color: #165DFF !important;
  border-color: #165DFF !important;
  color: #FFFFFF !important;
  border-radius: 4px !important;
}

.save-btn:hover {
  background-color: #4080ff !important;
  border-color: #4080ff !important;
  color: #FFFFFF !important;
}

.cancel-btn {
  background-color: #F3F3F3 !important;
  border-color: #F3F3F3 !important;
  color: #101010 !important;
  border-radius: 4px !important;
  margin-left: 8px;
}

.cancel-btn:hover {
  background-color: #F3F3F3 !important;
  border-color: #F3F3F3 !important;
  color: #101010 !important;
}

.create-form-modal :deep(.ant-input),
.create-form-modal :deep(.ant-input:hover),
.create-form-modal :deep(.ant-input:focus) {
  border-radius: 4px 4px 4px 4px !important;
  border: 1px solid #C7C7C7 !important;
}

.create-form-modal :deep(.ant-input-affix-wrapper),
.create-form-modal :deep(.ant-input-affix-wrapper:hover),
.create-form-modal :deep(.ant-input-affix-wrapper-focused) {
  border-radius: 4px 4px 4px 4px !important;
  border: 1px solid #C7C7C7 !important;
}

.create-form-modal :deep(.ant-input-affix-wrapper .ant-input) {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

.create-form-modal :deep(.ant-select-selector),
.create-form-modal :deep(.ant-select-selector:hover),
.create-form-modal :deep(.ant-select-focused .ant-select-selector) {
  border-radius: 4px 4px 4px 4px !important;
  border: 1px solid #C7C7C7 !important;
}

.create-form-modal :deep(.ant-picker),
.create-form-modal :deep(.ant-picker:hover),
.create-form-modal :deep(.ant-picker-focused) {
  border-radius: 4px 4px 4px 4px !important;
  border: 1px solid #C7C7C7 !important;
}

.create-form-modal :deep(.ant-input-number),
.create-form-modal :deep(.ant-input-number:hover),
.create-form-modal :deep(.ant-input-number-focused) {
  border-radius: 4px 4px 4px 4px !important;
  border: 1px solid #C7C7C7 !important;
  width: 100% !important;
}

.create-form-modal :deep(.ant-input-number-input) {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

.create-form-modal :deep(.ant-form) {
  padding: 0 12px 0px 12px !important;
  margin-bottom: 0 !important;
}
</style>

<style>
/* 全局样式确保输入框样式生效 */
.create-form-modal .ant-input,
.create-form-modal .ant-input:hover,
.create-form-modal .ant-input:focus {
  border-radius: 4px 4px 4px 4px !important;
  border: 1px solid #C7C7C7 !important;
}

.create-form-modal .ant-input-affix-wrapper,
.create-form-modal .ant-input-affix-wrapper:hover,
.create-form-modal .ant-input-affix-wrapper-focused {
  border-radius: 4px 4px 4px 4px !important;
  border: 1px solid #C7C7C7 !important;
}

.create-form-modal .ant-input-affix-wrapper .ant-input {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

.create-form-modal .ant-select-selector,
.create-form-modal .ant-select-selector:hover,
.create-form-modal .ant-select-focused .ant-select-selector {
  border-radius: 4px 4px 4px 4px !important;
  border: 1px solid #C7C7C7 !important;
}

.create-form-modal .ant-picker,
.create-form-modal .ant-picker:hover,
.create-form-modal .ant-picker-focused {
  border-radius: 4px 4px 4px 4px !important;
  border: 1px solid #C7C7C7 !important;
}

.create-form-modal .ant-input-number,
.create-form-modal .ant-input-number:hover,
.create-form-modal .ant-input-number-focused {
  border-radius: 4px 4px 4px 4px !important;
  border: 1px solid #C7C7C7 !important;
  width: 100% !important;
}

.create-form-modal .ant-input-number-input {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

.create-form-modal .ant-modal-body {
  padding: 20px 16px 80px 16px !important;
}

/* 更强的选择器确保样式生效 */
.ant-modal.create-form-modal .ant-modal-body {
  padding: 20px 16px 80px 16px !important;
}

/* 最高优先级选择器 */
body .ant-modal.create-form-modal .ant-modal-body {
  padding-bottom: 80px !important;
}
</style>
